import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { InvoicePeerService } from '../../service/invoice-peer.service';
import { combineLatest, forkJoin, Observable, of, Subscription } from 'rxjs';
import { EndCustomer as LegacyEndCustomer } from '../../model/end-customer';
import { MatSelectChange } from '@angular/material/select';
import { startWith, switchMap, tap, timeout } from 'rxjs/operators';
import { CompanyLookupResource } from '../../model/lookup-resource';
import { CompanyLookup } from '../../model/company-lookup';
import { canonicalizeBusinessIdControl, endCustomerBusinessIdValidator } from 'src/app/validators/business-id/angular-adapter';
import { EinvoiceAddressLookupService, EndCustomersService, InvoicesService } from 'src/app/api/services';
import { EInvoiceAddressOption, EInvoiceAddressOptionsResponse, EndCustomer, EndCustomerContact, Invoice, CreateEndCustomer, Permission } from 'src/app/api/models';
import { UserService } from 'src/app/service/user.service';
import { AAARatings } from 'src/app/enum/aaa-ratings';
import { countryName, CountryOption, endCustomerCountryOptions } from 'src/app/shared/countries/end-customer-countries';

/**
 * The public lifecycle tag of the ambient eInvoice routing resolution (design
 * §2.1/2.2): `idle` before any resolve, `loading` while a request (or one silent
 * retry) is in flight, `resolved` once candidates are back, `failed` once every
 * attempt has failed (the fail-open `? unverified` state). It mirrors the
 * internal FSM (`LookupState`) tag one-to-one.
 */
export type EinvoiceLookupState = 'idle' | 'loading' | 'resolved' | 'failed';

/**
 * Tri-state freshness of the *stored* routing plus the two transient states
 * (design Decision 2). `none` means there is no stored routing to describe (no
 * badge). `unverified` (Apix unreachable) is deliberately never conflated with
 * `not_registered` (Apix answered; routing genuinely absent).
 */
export type EinvoiceFreshness = 'none' | 'loading' | 'registered' | 'not_registered' | 'unverified';

/**
 * Client-side ceiling for the ambient lookup. Set above the backend
 * addressquery bound (SEND_READINESS_ADDRESSQUERY_TIMEOUT, default 5s) so a
 * reachable-but-slow Apix still returns the structured `apix_unavailable`
 * (nicely labelled) and this timeout only catches a truly hung proxy — either
 * way the picker degrades to `? unverified` (design Decision 7.2).
 *
 * The ordering (backend bound < this ceiling) is an enforced invariant, not
 * just a comment: the backend rejects a SEND_READINESS_ADDRESSQUERY_TIMEOUT at
 * or above this value at startup (coolapi.Config.Validate /
 * EInvoiceLookupClientCeiling, issue #84 review finding 7). Keep this constant in lockstep
 * with EInvoiceLookupClientCeiling in apps/cctool-go/internal/coolapi/config.go
 * if either moves.
 */
export const EINVOICE_LOOKUP_TIMEOUT_MS = 8000;

/** Delay before the single automatic retry on a failed lookup (Decision 7.3). */
export const EINVOICE_AUTO_RETRY_DELAY_MS = 1500;

/** A routing pair, trim-normalized; compared by equality only (design Decision 10). */
export interface Routing {
  operator: string;
  address: string;
}

/**
 * Explicit lifecycle of the ambient eInvoice resolution (design Decision 10).
 * The `resolved` variant carries the pair it judged (`verdictPair`) and the
 * server verdict (`present`), so a verdict can never be rendered against a
 * routing it did not describe. Business-id validation is synchronous (a checksum
 * `ValidatorFn`), so the control settles straight to VALID/INVALID — there is no
 * PENDING window to model.
 */
export type LookupState =
  | { tag: 'idle' }
  | { tag: 'loading'; businessId: string; retriedOnce: boolean; storedPair: Routing }
  | { tag: 'failed'; businessId: string }
  | { tag: 'resolved'; businessId: string; options: EInvoiceAddressOption[]; verdictPair: Routing; present: boolean | null };

/**
 * Inputs to the lookup FSM. Value-carrying events supply what `reduce` needs.
 * The fire-producing events (`bizIdSettled`, `retry`, `saved`) carry the
 * `storedPair` snapshotted **at dispatch** (design Decision 11 Axis 2): the
 * request is bound to the routing the form held when the reduce decided to
 * fire, so a dispatched lookup can never read a form value newer than its own
 * dispatch (round-6 finding 5).
 */
export type LookupEvent =
  | { type: 'bizIdSettled'; id: string; status: string; storedPair: Routing }
  | { type: 'lookupOk'; response: EInvoiceAddressOptionsResponse; askedPair: Routing }
  | { type: 'lookupErr' }
  | { type: 'retry'; id: string; invalid: boolean; storedPair: Routing }
  | { type: 'saved'; id: string; invalid: boolean; storedPair: Routing }
  | { type: 'autoRetryFired' }
  | { type: 'cancel' };

/** Side-effects the reducer asks the component to run (subscriptions/timers). */
export type LookupEffect =
  | { kind: 'none' }
  | { kind: 'cancel' }
  | { kind: 'fire'; businessId: string; storedPair: Routing }
  | { kind: 'scheduleRetry'; businessId: string };

const NO_EFFECT: LookupEffect = { kind: 'none' };

/** The id a request has been dispatched (or resolved/failed) for; else null. */
function anchorBusinessId(state: LookupState): string | null {
  return state.tag === 'loading' || state.tag === 'failed' || state.tag === 'resolved'
    ? state.businessId
    : null;
}

/**
 * Pure transition for the ambient lookup FSM (design Decision 10): the next
 * state plus the side-effect to run. Every guard rounds 2–4 added ad hoc is a
 * property here — one-request-in-flight, held-per-id (incl. `failed`, review
 * round-4 finding 3), clear-stale-on-id-change (review finding 5), id-round-trip anchor reset
 * (round-3 finding 2), and settled-INVALID → idle (round-4 finding 5).
 */
export function reduceLookup(state: LookupState, event: LookupEvent): { next: LookupState; effect: LookupEffect } {
  switch (event.type) {
    case 'bizIdSettled': {
      if (!event.id) {
        return { next: { tag: 'idle' }, effect: { kind: 'cancel' } };
      }
      const changed = event.id !== anchorBusinessId(state);
      if (event.status !== 'VALID') {
        // Settled INVALID: drop to idle and *always* cancel — a `→ idle`
        // transition can never have a live request worth preserving, so an
        // unchanged anchored id must not leak its in-flight subscription + 8s
        // timeout (round-6 finding 3 / Decision 11 Axis 2).
        return { next: { tag: 'idle' }, effect: { kind: 'cancel' } };
      }
      // VALID.
      if (!changed) {
        // Held-per-id: a repeat trigger for the anchored id is a no-op, holding
        // loading/resolved/failed alike (round-4 finding 3).
        return { next: state, effect: NO_EFFECT };
      }
      return {
        next: { tag: 'loading', businessId: event.id, retriedOnce: false, storedPair: event.storedPair },
        effect: { kind: 'fire', businessId: event.id, storedPair: event.storedPair },
      };
    }
    case 'retry':
    case 'saved': {
      // Forced resolve (manual retry / re-check / post-Save / create-from-id):
      // bypass the held-guard, but no-op on a blank/non-VALID id (`invalid` is
      // derived from `status !== 'VALID'`, so an INVALID id cannot fire — round-6 finding 4).
      if (!event.id || event.invalid) {
        return { next: state, effect: NO_EFFECT };
      }
      return {
        next: { tag: 'loading', businessId: event.id, retriedOnce: false, storedPair: event.storedPair },
        effect: { kind: 'fire', businessId: event.id, storedPair: event.storedPair },
      };
    }
    case 'autoRetryFired':
      // The single silent retry fires; stay `loading` (retriedOnce already set).
      // Re-uses the pair snapshotted at the original dispatch, so the retry can
      // never read a form value edited during the retry window (Decision 11).
      return state.tag === 'loading'
        ? { next: state, effect: { kind: 'fire', businessId: state.businessId, storedPair: state.storedPair } }
        : { next: state, effect: NO_EFFECT };
    case 'lookupOk':
      // Ignore a late response for a superseded state (its sub is unsubscribed).
      return state.tag === 'loading'
        ? {
            next: {
              tag: 'resolved',
              businessId: state.businessId,
              options: event.response.options ?? [],
              verdictPair: event.askedPair,
              present: event.response.stored_routing_present ?? null,
            },
            effect: NO_EFFECT,
          }
        : { next: state, effect: NO_EFFECT };
    case 'lookupErr':
      if (state.tag !== 'loading') {
        return { next: state, effect: NO_EFFECT };
      }
      // Fail open (Decision 7): retry once silently, then settle `failed`. The
      // pending retry re-uses the snapshotted pair (carried on the loading state).
      return state.retriedOnce
        ? { next: { tag: 'failed', businessId: state.businessId }, effect: NO_EFFECT }
        : { next: { tag: 'loading', businessId: state.businessId, retriedOnce: true, storedPair: state.storedPair }, effect: { kind: 'scheduleRetry', businessId: state.businessId } };
    case 'cancel':
      return { next: { tag: 'idle' }, effect: { kind: 'cancel' } };
  }
}

function routingEq(a: Routing, b: Routing): boolean {
  return a.operator === b.operator && a.address === b.address;
}

/**
 * Canonical client-side routing normalization — trim **+ upper-case** —
 * mirroring the server's `apixeinvoice.normalizeRouting` (routing.go). It MUST
 * be applied in *every* comparison of a form routing against a server-returned
 * option, or the client's membership check drifts case-sensitively from the
 * case-insensitive `matches_stored` / `stored_routing_present` the server
 * already computed — a card would show "✓ saved" yet not highlight, and once
 * the form leaves `verdictPair` the membership branch would read `none` where
 * the server says `registered` (round-6 finding 2 / Decision 11 Axis 1). Comparison
 * rule only: values *written* to the form on a card pick keep the option's raw
 * casing (see `applyEinvoiceAddress`).
 */
function normalizeRouting(s: string): string {
  return (s ?? '').trim().toUpperCase();
}

/**
 * A candidate's routing pair. The operator half is the candidate's
 * `operator` — the välittäjätunnus — never `operator_id`, which is an
 * Apix-internal operator code (a mnemonic such as `PAGERO` for some
 * operators) with no routing role. This must stay the same half the server's
 * `matches_stored` keys on, or the card highlight drifts from the verdict.
 */
function optionRouting(option: EInvoiceAddressOption): Routing {
  return { operator: normalizeRouting(option.operator ?? ''), address: normalizeRouting(option.address ?? '') };
}

/** Normalize a raw routing pair for equality (design Decision 10 / 11 Axis 1). */
function normalizeRoutingPair(raw: Routing): Routing {
  return { operator: normalizeRouting(raw.operator), address: normalizeRouting(raw.address) };
}

function isRoutingAmongOptions(formPair: Routing, options: EInvoiceAddressOption[]): boolean {
  return options.some((o) => routingEq(optionRouting(o), formPair));
}

/**
 * Tri-state freshness of the current form routing (design Decision 2 / 6 / 10),
 * from the FSM state + the current form routing. **Stance-A (issue #84 round-5):**
 * a resolved verdict is inseparable from the routing it judged — the server's
 * `stored_routing_present` flag applies **only** when the form still holds the
 * `verdictPair` the lookup asked about; a form moved to another live candidate
 * reads `registered` by *list membership* (no `RoutingMatches` re-derivation),
 * and any other pair (a manually-typed non-candidate) is `none`. So a confirmed
 * `registered`/`not_registered` verdict is returned only when
 * `formPair === verdictPair` **or** `formPair ∈ options`. `unverified` (Apix
 * unreachable) is never conflated with `not_registered`.
 */
export function deriveEinvoiceFreshness(state: LookupState, rawFormPair: Routing): EinvoiceFreshness {
  // Normalize both sides of every comparison with the canonical trim+upper rule
  // (Decision 11 Axis 1): the caller may pass a raw form pair, and the stored
  // verdictPair is re-normalized so a case-only form change never falls out of
  // the judged-pair branch.
  const formPair = normalizeRoutingPair(rawFormPair);
  const hasStored = formPair.operator !== '' && formPair.address !== '';
  if (!hasStored) {
    return state.tag === 'loading' ? 'loading' : 'none';
  }
  switch (state.tag) {
    case 'loading':
      return 'loading';
    case 'failed':
      return 'unverified';
    case 'resolved':
      if (routingEq(formPair, normalizeRoutingPair(state.verdictPair))) {
        // The form still holds the judged pair: the server verdict is authoritative.
        if (state.present === true) {
          return 'registered';
        }
        if (state.present === false) {
          return 'not_registered';
        }
        return 'none';
      }
      // The form moved off the judged pair. A live candidate is registered by
      // membership; anything else is unjudged, so no confirmed verdict.
      return isRoutingAmongOptions(formPair, state.options) ? 'registered' : 'none';
    default:
      return 'none';
  }
}

/**
 * The highlighted candidate: the option whose routing equals the form's current
 * routing (the pick, or the stored routing in view mode). Pure membership — no
 * `matches_stored` fallback, so a manually-typed non-candidate highlights no
 * card (design Decision 10, round-5 finding 2). The `matches_stored` marker is a
 * separate, server-driven concern rendered per card.
 */
export function deriveSelectedEinvoiceCard(state: LookupState, rawFormPair: Routing): EInvoiceAddressOption | null {
  if (state.tag !== 'resolved') {
    return null;
  }
  const formPair = normalizeRoutingPair(rawFormPair);
  return state.options.find((o) => routingEq(optionRouting(o), formPair)) ?? null;
}

@Component({
  selector: 'app-invoice-end-customer-widget',
  templateUrl: './invoice-end-customer-widget.component.html',
  styleUrls: ['./invoice-end-customer-widget.component.scss']
})
export class InvoiceEndCustomerWidgetComponent implements OnInit, OnDestroy {

  get widgetTitle(): string {
    return 'End Customer' + (this.endCustomer?.company_situation ? ' ⚠️' : '');
  }

  /**
   * The Finnish name of a stored country, for the view-mode label (issue
   * #619): any code the server takes, and any other value as it is (issue
   * #626).
   */
  public readonly countryName = countryName;

  private countryOptionsFor?: { stored: string | undefined; options: readonly CountryOption[] };

  /**
   * The Country selects' options: the console's list (issue #619), plus the
   * stored end customer's country when it is off the list. Kept per stored
   * value, so change detection hands the selects the same array.
   */
  public get countryOptions(): readonly CountryOption[] {
    const stored = this.endCustomer?.country;
    if (!this.countryOptionsFor || this.countryOptionsFor.stored !== stored) {
      this.countryOptionsFor = { stored, options: endCustomerCountryOptions(stored) };
    }
    return this.countryOptionsFor.options;
  }

  invoice: Invoice;
  endCustomer: EndCustomer;
  endCustomerContact: EndCustomerContact;
  endCustomerContacts: EndCustomerContact[];

  // Single source of truth for the ambient eInvoice resolution lifecycle
  // (design Decision 10). The verdict (`present`) is inseparable from the
  // routing it judged (`verdictPair`), and the draft routing stays owned by the
  // form (Decision 1) — the FSM never stores it. Mutated only via dispatchLookup.
  private lookupState: LookupState = { tag: 'idle' };

  // True when the operator has opted into raw operator/address entry via the
  // "Edit manually" toggle. The fields are also auto-revealed (see
  // `einvoiceManualAutoRevealed`) when there is nothing to pick and nothing
  // stored, so the operator is never trapped.
  einvoiceManualEntry = false;

  private einvoiceLookupSub?: Subscription;
  private einvoiceRetryTimer?: ReturnType<typeof setTimeout>;
  private einvoiceTriggerSub?: Subscription;

  // Suppresses the ambient business-id trigger while `patchEndCustomer` bulk-
  // patches the end-customer group, so the lookup never dispatches against a
  // half-patched form (design Decision 13, round-7 finding 1). `FormGroup.patchValue`
  // patches children in key order and each leaf emits synchronously, so
  // `business_id` (patched first) would otherwise fire `onBusinessIdSettled`
  // while the sibling `einvoice_address` is still blank — snapshotting a blank
  // storedPair and mis-reading a de-registered routing as `none`. The patch sets
  // this true, then fires a single coherent settle against the populated form.
  private suppressEinvoiceTrigger = false;

  // --- eInvoice lookup compatibility accessors -------------------------------
  // `lookupState` is the single owner; these read-only projections preserve the
  // historical field surface the template + specs bind to.

  /** Public lifecycle tag, projected one-to-one from the internal FSM. */
  get einvoiceLookupState(): EinvoiceLookupState {
    return this.lookupState.tag;
  }

  /** Live Apix routing candidates from the most recent resolution (else none). */
  get einvoiceAddressChoices(): EInvoiceAddressOption[] {
    return this.lookupState.tag === 'resolved' ? this.lookupState.options : [];
  }

  aaaRatings = AAARatings;

  locked = false;
  edited = false;
  isCreating = false;

  /**
   * Whether the user may add an end customer, which creates it through
   * POST /end-customers (end-customers:create, issue #533). Unknown, not
   * missing, until the current user loads.
   */
  public readonly canCreateEndCustomer$: Observable<boolean>;
  public readonly createPermission = Permission.EndCustomersCreate;

  /**
   * Whether the user may save an end customer, which saves it through
   * PUT /end-customers/{id} (end-customers:edit, issue #620): the edit
   * form's Save, whose first request that is, and the "Hold to update
   * timestamp" button. Unknown, not missing, until the current user loads.
   */
  public readonly canEditEndCustomer$: Observable<boolean>;
  public readonly editPermission = Permission.EndCustomersEdit;

  selectingEndCustomer = false;

  form: UntypedFormGroup;

  constructor(
    private builder: UntypedFormBuilder,
    private invoicePeerService: InvoicePeerService,
    private endCustomersService: EndCustomersService,
    private invoicesService: InvoicesService,
    private einvoiceAddressLookupService: EinvoiceAddressLookupService,
    private companyLookupResource: CompanyLookupResource,
    userService: UserService
  ) {
    this.canCreateEndCustomer$ = userService.hasPermission(this.createPermission);
    this.canEditEndCustomer$ = userService.hasPermission(this.editPermission);
    this.form = this.builder.group({
      endCustomer: this.builder.group({
        business_id: [null, [Validators.required, endCustomerBusinessIdValidator()]],
        vat_number: [null, Validators.required],
        name: [null, Validators.required],
        department: [null],
        street: [null, Validators.required],
        zip: [null, Validators.required],
        city: [null, Validators.required],
        country: ['FI', Validators.required],
        company_form_code: [null],
        company_form_other: [null],
        einvoice_address: this.builder.group({
          address: [null],
          operator: [null],
        }),
        credit_limit: this.builder.group({
          limit: this.builder.group({
            amount: [null, Validators.min(0)],
            currency: ['EUR'],
          }),
        }),
        aaa_rating: this.builder.group({
          category: [null],
        }),
      }),
      contact: this.builder.group({
        name: [null],
        email: [null, Validators.email],
        phone: [null]
      })
    });

    this.form.get('endCustomer.country').valueChanges
      .subscribe((country) => {
        const businessId = this.form.get('endCustomer.business_id');
        if (businessId) {
          businessId.markAsTouched();
          businessId.updateValueAndValidity();
        }

        const companyFormCode = this.form.get('endCustomer.company_form_code');
        const companyFormOther = this.form.get('endCustomer.company_form_other');

        companyFormCode.clearValidators();
        companyFormOther.clearValidators();

        if (country === 'FI') {
          companyFormOther.setValue(null);
        } else {
          companyFormCode.setValue(null);
        }
        companyFormCode.updateValueAndValidity();
        companyFormOther.updateValueAndValidity();
      });
  }

  edit(): void {
    this.invoicePeerService.lock();
    this.edited = true;
    if (this.endCustomer) {
      this.patchEndCustomer(this.endCustomer);
    }
    if (this.endCustomerContact) {
      this.patchEndCustomerContact(this.endCustomerContact);
    }
  }

  revert(): void {
    this.edited = false;
    this.selectingEndCustomer = false;
    this.einvoiceManualEntry = false;
    this.isCreating = false;
    // Cancel any in-flight lookup + pending auto-retry so no stray request or
    // `checking…` fires in view mode after the abandoned edit (round-5 finding 4). This
    // is the single path that both unsubscribes the request and clears the retry
    // timer. `load()` re-patches the stored routing next, so the selected card
    // derives cleanly from the restored form pair — no canceled pick leaks into
    // view mode (round-3 finding 1), now without keeping the stale candidate set.
    this.dispatchLookup({ type: 'cancel' });
    this.invoicePeerService.unlock();
    this.load(this.invoice.end_customer_id, this.invoice.end_customer_contact_id);
  }

  createEndCustomer() {
    this.selectingEndCustomer = false;
    this.isCreating = true;
    this.endCustomer = null;
    this.endCustomerContacts = [];
    this.endCustomerContact = null;
    this.form.reset();
    this.form.get('endCustomer').patchValue({ country: 'FI' });
  }

  createEndCustomerFromBusinessId(): void {
    if (this.form.get('endCustomer.business_id').invalid) {
      return;
    }
    // Operator-entered create: canonicalize the typed value before submitting.
    canonicalizeBusinessIdControl(this.form.get('endCustomer.business_id'));
    const newEndCustomer: CreateEndCustomer = {
      business_id: this.form.get('endCustomer.business_id').value,
      country: this.form.get('endCustomer.country').value,
      contacts: [{ name: '', email: '', phone: '' }]
    };

    this.endCustomersService.createEndCustomer({ body: newEndCustomer })
      .subscribe(createdEndCustomer => {
        this.endCustomer = createdEndCustomer;
        this.patchEndCustomer(this.endCustomer);
        this.isCreating = false;

        this.endCustomerContacts = createdEndCustomer.contacts;
        this.endCustomerContact = this.endCustomerContacts[0];
        this.patchEndCustomerContact(this.endCustomerContact);

        // Re-resolve for the freshly created end customer now that the eInvoice
        // section is visible (Decision 9). Forced so it runs even though the
        // ambient trigger may have already resolved this id during typing.
        this.forceResolveEinvoice('retry');
      });
  }


  selectEndCustomer() {
    this.selectingEndCustomer = true;
  }

  endCustomerSelected(endCustomer?: LegacyEndCustomer) {
    this.selectingEndCustomer = false;
    this.isCreating = false;
    this.convertFromLegacyEndCustomer(endCustomer);
    this.getEndCustomerContacts(endCustomer.endCustomerId)
      .subscribe(endCustomerContacts => {
        this.endCustomerContacts = endCustomerContacts;
        if (endCustomerContacts.length > 0) {
          this.endCustomerContact = endCustomerContacts[0];
          this.patchEndCustomerContact(this.endCustomerContact);
        }
      });
  }

  convertFromLegacyEndCustomer(from: LegacyEndCustomer) {
    if (!from) {
      return {id: 0, aaa_rating: {category: null, created_at: null}};
    }

    this.endCustomersService.getEndCustomer({id: from.endCustomerId}).subscribe((endCustomer: EndCustomer) => {
      this.endCustomer = endCustomer;
      this.patchEndCustomer(this.endCustomer);
    });
  }

  createEndCustomerContact() {
    // An unsaved contact: the server gives it its id and created_at when the
    // widget saves it, and JSON drops the undefined ones from the request.
    this.endCustomerContact = { id: undefined, created_at: undefined, end_customer_id: this.endCustomer.id };
    this.patchEndCustomerContact(this.endCustomerContact);
    this.endCustomerContacts.push(this.endCustomerContact);
  }

  removeEndCustomerContact() {
    const contactToRemove = this.endCustomerContact;
    const index = this.endCustomerContacts.indexOf(contactToRemove);
    if (index === -1) { return; }

    const onLocalRemove = () => {
      this.endCustomerContacts.splice(index, 1);
      if (this.endCustomerContacts.length > 0) {
        this.endCustomerContact = this.endCustomerContacts[0];
        this.patchEndCustomerContact(this.endCustomerContact);
      } else {
        this.endCustomerContact = null;
        this.form.get('contact').reset();
      }
    };

    if (contactToRemove.id) {
      this.endCustomersService.deleteEndCustomerContact({
        end_customer_id: contactToRemove.end_customer_id,
        id: contactToRemove.id
      }).pipe(
        switchMap(() => {
          if (this.invoice.end_customer_contact_id === contactToRemove.id) {
            return this.invoicesService.deleteInvoiceEndCustomerContact({ id: this.invoice.id });
          }
          return of(null);
        })
      ).subscribe(updatedInvoice => {
        if (updatedInvoice) {
          // push updated invoice to peer, which will update our local `this.invoice`
          this.invoicePeerService.invoiceChanged(updatedInvoice);
        }
        // now remove locally
        onLocalRemove();
      });
    } else {
      onLocalRemove();
    }
  }

  endCustomerContactChanged(event: MatSelectChange) {
    Object.assign(this.endCustomerContact, this.form.value.contact);
    this.endCustomerContact = event.value;
    this.patchEndCustomerContact(this.endCustomerContact);
  }

  lookupCompany(): void {
    this.companyLookupResource
      .lookup(this.form.get('endCustomer.business_id').value)
      .subscribe((lookup: CompanyLookup) => {
        this.form.get('endCustomer.street').setValue(lookup.street);
        this.form.get('endCustomer.zip').setValue(lookup.zip);
        this.form.get('endCustomer.city').setValue(lookup.city);
        this.form.get('endCustomer.country').setValue(lookup.country);
      });
  }

  // ---------------------------------------------------------------------------
  // Ambient eInvoice routing resolution (design §2/§3/§5, Decision 10). The
  // resolution is a non-blocking side-channel: it never gates a form control,
  // validity, or Save (Decision 1). Its whole lifecycle lives in `lookupState`,
  // mutated only through `dispatchLookup` — the one-request-in-flight,
  // held-per-id, clear-stale-on-id-change, id-round-trip-anchor, and
  // VALID-vs-INVALID rules are all properties of the pure `reduceLookup`.
  // ---------------------------------------------------------------------------

  /** Apply one lookup event: reduce to the next state, then run its effect. */
  private dispatchLookup(event: LookupEvent): void {
    const { next, effect } = reduceLookup(this.lookupState, event);
    this.lookupState = next;
    this.runLookupEffect(effect);
  }

  private runLookupEffect(effect: LookupEffect): void {
    switch (effect.kind) {
      case 'none':
        return;
      case 'cancel':
        this.cancelEinvoiceLookup();
        return;
      case 'scheduleRetry':
        this.einvoiceRetryTimer = setTimeout(
          () => this.dispatchLookup({ type: 'autoRetryFired' }), EINVOICE_AUTO_RETRY_DELAY_MS);
        return;
      case 'fire':
        this.fireEinvoiceLookup(effect.businessId, effect.storedPair);
        return;
    }
  }

  /**
   * React to the business-id trigger. The control state is read *fresh* (not the
   * possibly-stale emitted tuple) so a stale VALID status paired with a
   * just-changed value can never fire a lookup for an unvalidated id. The
   * blank/INVALID/VALID branching lives in `reduceLookup`.
   */
  private onBusinessIdSettled(): void {
    // Skip the mid-patch emits from `patchEndCustomer`'s bulk patchValue; the
    // patch fires one coherent settle itself once the whole group is populated
    // (design Decision 13). Every leaf emit during the patch early-returns here.
    if (this.suppressEinvoiceTrigger) {
      return;
    }
    const control = this.form.get('endCustomer.business_id');
    const id = ((control.value as string) ?? '').trim();
    if (!id) {
      // Blank id collapses any opened manual panel (old resetEinvoiceLookup).
      this.einvoiceManualEntry = false;
    }
    this.dispatchLookup({ type: 'bizIdSettled', id, status: control.status, storedPair: this.einvoiceFormStoredPair() });
  }

  /** Manual `↻ Retry` / `↻ Re-check` — re-runs the resolution even if held. */
  retryEinvoiceLookup(): void {
    this.forceResolveEinvoice('retry');
  }

  /**
   * Forced resolution (manual retry / re-check / post-Save / create-from-id):
   * bypasses the held-per-id guard, but is still a no-op on a blank or non-VALID
   * id. The gate is `status !== 'VALID'` so a forced Re-check can never fire for
   * an INVALID id (round-6 finding 4 / Decision 11 Axis 2). The pair is snapshotted
   * here, at dispatch, and carried into the fire effect.
   */
  private forceResolveEinvoice(kind: 'retry' | 'saved'): void {
    const control = this.form.get('endCustomer.business_id');
    const id = ((control.value as string) ?? '').trim();
    this.dispatchLookup({ type: kind, id, invalid: !id || control.status !== 'VALID', storedPair: this.einvoiceFormStoredPair() });
  }

  /** Snapshot the form's raw stored routing at dispatch (design Decision 11). */
  private einvoiceFormStoredPair(): Routing {
    return { operator: this.currentEinvoiceOperator(), address: this.currentEinvoiceAddress() };
  }

  private fireEinvoiceLookup(businessId: string, storedPair: Routing): void {
    // Supersede any in-flight request + pending auto-retry so a forced or
    // round-tripped resolution never races a stale response (round-3/4).
    this.cancelEinvoiceLookup();
    // Send the pair snapshotted at dispatch — never a fresh form read — so the
    // request is bound to the routing the form held when the reduce decided to
    // fire (round-6 finding 5 / Decision 11 Axis 2). The trim/normalize of that pair is
    // the verdictPair the response is bound to (design Decision 10).
    const askedPair = normalizeRoutingPair(storedPair);

    this.einvoiceLookupSub = this.einvoiceAddressLookupService
      .getEinvoiceAddressOptions({
        business_id: businessId,
        address: storedPair.address || undefined,
        operator: storedPair.operator || undefined,
      })
      .pipe(timeout(EINVOICE_LOOKUP_TIMEOUT_MS))
      .subscribe({
        next: (response) => this.onEinvoiceOptionsResolved(response, askedPair),
        error: () => this.dispatchLookup({ type: 'lookupErr' }),
      });
  }

  private onEinvoiceOptionsResolved(response: EInvoiceAddressOptionsResponse, askedPair: Routing): void {
    this.dispatchLookup({ type: 'lookupOk', response, askedPair });

    // Sole-candidate convenience (Decision 4): auto-fill a lone candidate only
    // into *blank* routing, and only while editing — a background resolve in
    // view mode must never mutate or dirty the form (review finding 1; the lookup
    // itself keeps running in view mode so the badge still renders). An
    // already-set routing is never silently overwritten; its card is surfaced
    // for explicit confirmation instead.
    if (this.edited && this.lookupState.tag === 'resolved' &&
        this.lookupState.options.length === 1 && this.isEinvoiceRoutingBlank()) {
      this.applyEinvoiceAddress(this.lookupState.options[0]);
    }
  }

  /** Selecting a candidate card writes its routing and dirties the form. */
  einvoiceCardSelected(option: EInvoiceAddressOption): void {
    this.applyEinvoiceAddress(option);
  }

  toggleEinvoiceManualEntry(): void {
    this.einvoiceManualEntry = !this.einvoiceManualEntry;
  }

  /**
   * Writes the picked candidate's routing into the form. The stored operator is
   * the candidate's `operator` (the välittäjätunnus) — the only operator value
   * meaningful outside Apix, and the one carried onward as the Finvoice
   * `ToIntermediator` and the Netvisor `FinvoiceRouterCode`. Writing
   * `operator_id` here stored Apix-internal codes such as `PAGERO`, which
   * Netvisor rejects outright ("E-invoice operator not valid: PAGERO").
   */
  private applyEinvoiceAddress(option: EInvoiceAddressOption): void {
    const group = this.form.get('endCustomer.einvoice_address');
    group.get('operator').setValue(option.operator);
    group.get('address').setValue(option.address);
    // Selecting a candidate is an explicit operator action: mark the form dirty
    // so Save is meaningful. The highlight follows the written routing via
    // deriveSelectedEinvoiceCard(formPair) — there is no separately tracked pick.
    group.markAsDirty();
  }

  private cancelEinvoiceLookup(): void {
    this.einvoiceLookupSub?.unsubscribe();
    this.einvoiceLookupSub = undefined;
    if (this.einvoiceRetryTimer) {
      clearTimeout(this.einvoiceRetryTimer);
      this.einvoiceRetryTimer = undefined;
    }
  }

  // --- eInvoice freshness view-model (Decision 2 / Decision 10) ---------------

  /**
   * Tri-state freshness of the stored routing (Decision 2), derived purely from
   * the FSM state + the current form routing. In view mode the form mirrors the
   * stored routing, so the same derivation serves both modes. This group keeps
   * the pre-round-5 server-flag verdict; the paired-verdict flip is §12C.
   */
  get einvoiceFreshness(): EinvoiceFreshness {
    return deriveEinvoiceFreshness(this.lookupState, this.currentEinvoiceRouting());
  }

  /**
   * The highlighted candidate: the option whose routing equals the form's
   * current routing (the pick, or the stored routing in view mode). Pure
   * membership (round-5 finding 2) — a manually-typed non-candidate highlights no card;
   * drives the card highlight only, never the freshness verdict (review finding 6).
   */
  get selectedEinvoiceCard(): EInvoiceAddressOption | null {
    return deriveSelectedEinvoiceCard(this.lookupState, this.currentEinvoiceRouting());
  }

  /**
   * The stored routing's operator, exactly as stored: the välittäjätunnus. It
   * reads off the same source as the address rendered beside it
   * (`endCustomer.einvoice_address`), so both halves of a line describing the
   * *stored* routing come from the stored routing, and neither varies with
   * lookup state (design decision 8).
   *
   * Deliberately NOT the candidate's Apix operator code. Substituting it made
   * this line flip from `003723609900` to `PAGERO` the moment the ambient
   * lookup resolved — two opaque, mutually indistinguishable identifiers, with
   * nothing to say which one is stored. The recognisable code is a separate
   * annotation (`einvoiceOperatorCodeAnnotation`), never a substitution.
   */
  einvoiceOperatorDisplay(): string {
    return this.endCustomer?.einvoice_address?.operator ?? '';
  }

  /**
   * The recognisable Apix operator code for the stored routing, to be rendered
   * subordinate to the välittäjätunnus (`003723609900 (PAGERO)`) — never in its
   * place. Empty unless a resolved candidate matches the stored routing and its
   * code genuinely differs from its välittäjätunnus, so the non-mnemonic
   * majority (where the two are equal) renders exactly as it did before the
   * annotation existed.
   *
   * The comparison is against the *card's* own `operator`, not the stored
   * value: a card is selected only when its routing already equals the stored
   * routing, so this is the same comparison and keeps the method self-contained.
   *
   * Necessarily absent for a stale stored routing — no card is selected there,
   * so no candidate's code describes it. That is correct: annotating a routing
   * Apix does not recognise with some other candidate's code would invent a
   * relationship that does not exist.
   */
  einvoiceOperatorCodeAnnotation(): string {
    const card = this.selectedEinvoiceCard;
    if (!card?.operator_id) {
      return '';
    }
    return normalizeRouting(card.operator_id) === normalizeRouting(card.operator ?? '') ? '' : card.operator_id;
  }

  /**
   * Manual routing fields are shown either by explicit opt-in, or auto-revealed
   * when there is nothing to pick and nothing stored so the operator is never
   * trapped (Decision 5) — e.g. Apix unavailable / no candidates on a new
   * customer, or no business id yet.
   */
  get einvoiceManualFieldsShown(): boolean {
    return this.einvoiceManualEntry || this.einvoiceManualAutoRevealed;
  }

  get einvoiceManualAutoRevealed(): boolean {
    // Reveal manual entry whenever the resolution has settled with nothing to
    // pick, so the operator is never trapped behind the "Edit manually" toggle
    // (Decision 5, review finding 2). The trigger is the *stable* settled-with-zero-
    // candidates fact, never live form blankness/freshness — otherwise the
    // first keystroke into an auto-revealed field would flip the condition,
    // `einvoiceManualFieldsShown` would go false, and the input would unmount
    // mid-entry, dropping focus and re-trapping the operator the reveal exists
    // to free (round-6 finding 1 / Decision 11 Axis 3).
    if (this.einvoiceAddressChoices.length !== 0) {
      return false;
    }
    // Apix answered with zero candidates: genuinely nothing to pick — reveal
    // whether the (now-stale) routing is stored or the customer is new.
    if (this.einvoiceLookupState === 'resolved') {
      return true;
    }
    // No business id yet (idle) or Apix unreachable (failed): reveal only when
    // nothing is *persisted* — a new customer has no saved value to fall back
    // on; with a stored routing we show the saved value behind the toggle
    // (fail-open mockup, Decision 7). Keyed on the persisted routing, which is
    // stable while the operator types into the revealed field.
    if (this.einvoiceLookupState === 'idle' || this.einvoiceLookupState === 'failed') {
      return !this.hasPersistedEinvoiceRouting();
    }
    return false;
  }

  /** Both routing halves blank — the guard for auto-apply and auto-reveal. */
  isEinvoiceRoutingBlank(): boolean {
    const { operator, address } = this.currentEinvoiceRouting();
    return !operator && !address;
  }

  /**
   * A *complete* stored routing: both operator and address non-blank. This is
   * an AND, matching the backend gates (invoice_send_readiness.go
   * `hasEInvoiceRouting` and einvoice_address_options.go `hasStored`) — a
   * half-filled routing is neither blank nor stored, so it is never mis-badged
   * `not_registered` here while the send rejects it as missing routing
   * (review finding 3).
   */
  hasStoredEinvoiceRouting(): boolean {
    const { operator, address } = this.currentEinvoiceRouting();
    return operator !== '' && address !== '';
  }

  /**
   * A *persisted* stored routing on the end-customer record — both halves
   * non-blank. Unlike the live form, this is stable across an edit session, so
   * it can key the manual-entry auto-reveal without flipping mid-typing
   * (round-6 finding 1). Mirrors the AND-of-both-halves backend gate.
   */
  private hasPersistedEinvoiceRouting(): boolean {
    const routing = this.endCustomer?.einvoice_address;
    return !!routing?.operator?.trim() && !!routing?.address?.trim();
  }

  /** The form's current eInvoice routing, canonically normalized for equality. */
  private currentEinvoiceRouting(): Routing {
    return normalizeRoutingPair({ operator: this.currentEinvoiceOperator(), address: this.currentEinvoiceAddress() });
  }

  private currentEinvoiceOperator(): string {
    return (this.form.get('endCustomer.einvoice_address.operator').value as string) ?? '';
  }

  private currentEinvoiceAddress(): string {
    return (this.form.get('endCustomer.einvoice_address.address').value as string) ?? '';
  }

  trimBusinessId(): void {
    const businessIdControl = this.form.get('endCustomer.business_id');
    if (businessIdControl.value) {
      businessIdControl.setValue(businessIdControl.value.trim());
    }
  }

  save(): void {
    // business_id is immutable after creation (end_customer.go), so only a
    // create may canonicalize the typed value; an update must submit the
    // stored value byte-identical (design D10). On edit the field is read-only
    // and pristine, so the helper would skip it anyway — the guard is explicit.
    if (!this.endCustomer?.id) {
      canonicalizeBusinessIdControl(this.form.get('endCustomer.business_id'));
    }

    // Update current endCustomer from form
    Object.assign(this.endCustomer, this.form.value.endCustomer);

    if (this.endCustomer.credit_limit?.limit && !this.endCustomer.credit_limit?.limit.currency) {
      this.endCustomer.credit_limit.limit.currency = 'EUR';
    }

    // AI: If credit limit amount is empty, remove the credit_limit object before saving
    // to prevent sending an invalid partial object to the API. If an amount is present,
    // mark it as checked for update.
    if (this.endCustomer.credit_limit && !this.endCustomer.credit_limit.limit?.amount) {
      this.endCustomer.credit_limit = null;
    } else if (this.endCustomer.credit_limit?.limit?.amount) {
      this.endCustomer.credit_limit.mark_checked = true;
    }

    // AI: If aaa_rating category is set, also mark it as checked for update.
    // If it's not set, nullify the object so we don't send an empty one.
    if (this.endCustomer.aaa_rating?.category) {
      this.endCustomer.aaa_rating.mark_checked = true;
    } else if (this.endCustomer.aaa_rating) {
      this.endCustomer.aaa_rating = null;
    }

    const saveEndCustomer$ = this.endCustomer.id
      ? this.endCustomersService.updateEndCustomer({ id: this.endCustomer.id, body: this.endCustomer })
      : this.endCustomersService.createEndCustomer({ body: this.endCustomer });

    saveEndCustomer$.pipe(
      tap(savedEndCustomer => Object.assign(this.endCustomer, savedEndCustomer)),
      switchMap(savedEndCustomer => {
        // Update the currently selected contact with form values before saving
        Object.assign(this.endCustomerContact, this.form.value.contact);

        const contactSaveObservables = this.endCustomerContacts.map(contact => {
          if (contact.id) {
            return this.endCustomersService.updateEndCustomerContact({ end_customer_id: savedEndCustomer.id, id: contact.id, body: contact });
          } else {
            return this.endCustomersService.createEndCustomerContact({ end_customer_id: savedEndCustomer.id, body: contact });
          }
        });
        if (contactSaveObservables.length === 0) {
          return of([]);
        }
        return forkJoin(contactSaveObservables);
      }),
      tap(savedContacts => {
        // update local array with new IDs
        // This relies on the order being preserved
        savedContacts.forEach((savedContact, index) => {
          Object.assign(this.endCustomerContacts[index], savedContact);
        });
      }),
      switchMap(() => {
        return this.invoicesService.updateInvoiceEndCustomerContact({
          id: this.invoice.id,
          body: { end_customer_contact_id: this.endCustomerContact.id }
        });
      })
    ).subscribe(() => {
      this.invoicePeerService.refresh();
      this.invoicePeerService.unlock();
      this.edited = false;
      this.selectingEndCustomer = false;
      this.einvoiceManualEntry = false;
      // Re-resolve the now-saved routing (design Decision 6 corollary,
      // round-4 finding 1). The badge keys off the server verdict for the *saved*
      // routing and the ambient lookup is held per business_id, but a pick+Save
      // changes what is saved without changing the id — so the held-per-id
      // guard would otherwise strand the stale pre-pick verdict (e.g. `⚠ not
      // registered` for a routing the operator just picked from the live list).
      // Forcing bypasses the held-guard; fireEinvoiceLookup's cancelEinvoiceLookup
      // also supersedes any scheduled auto-retry, so no stray request or
      // transient `checking…` outlives the edit session (round-4 finding 4).
      this.forceResolveEinvoice('saved');
    });
  }

  patchEndCustomer(endCustomer: EndCustomer): void {
    // Suppress the ambient einvoice trigger across the bulk patch, then settle
    // once against the fully-populated form (design Decision 13, round-7 finding 1).
    // `business_id` and `einvoice_address` land in the same patchValue but emit
    // synchronously in key order, so a naive dispatch would snapshot a blank
    // routing. Suppressing + one explicit settle makes "populate fully, then
    // resolve" a structural property of the patch, not an emergent key-order one.
    // NOT `{ emitEvent: false }` — that would also silence the country→business_id
    // revalidation subscription (a wider blast radius than the einvoice trigger).
    this.suppressEinvoiceTrigger = true;
    // Reset in `finally` so the suppression is lifted even if `patchValue`
    // throws mid-construction. A stuck-true flag would silently early-return
    // every later `onBusinessIdSettled()` and kill the ambient lookup for the
    // component's lifetime (a dead feature `forceResolveEinvoice` would mask on
    // manual retry). The reset is a structural guarantee of the suppress/settle
    // discipline, not a "no code between raise and reset ever throws" assumption.
    try {
      this.form.patchValue({
        endCustomer: {
          business_id: endCustomer.business_id,
          vat_number: endCustomer.vat_number,
          name: endCustomer.name,
          department: endCustomer.department,
          street: endCustomer.street,
          zip: endCustomer.zip,
          city: endCustomer.city,
          country: endCustomer.country,
          company_form_code: endCustomer.company_form_code,
          company_form_other: endCustomer.company_form_other,
          einvoice_address: {
            address: endCustomer.einvoice_address?.address,
            operator: endCustomer.einvoice_address?.operator,
          },
          credit_limit: {
            limit: {
              amount: endCustomer.credit_limit?.limit.amount,
              currency: endCustomer.credit_limit?.limit.currency,
            },
            checked_at: endCustomer.credit_limit?.checked_at,
          },
          aaa_rating: {
            category: endCustomer.aaa_rating?.category,
            checked_at: endCustomer.aaa_rating?.checked_at,
          }
        },
        contact: this.form.value.contact
      });
    } finally {
      this.suppressEinvoiceTrigger = false;
    }
    this.onBusinessIdSettled();
  }

  patchEndCustomerContact(contact: EndCustomerContact): void {
    this.form.patchValue({
      endCustomer: this.form.value.endCustomer,
      contact: {
        name: contact.name,
        phone: contact.phone,
        email: contact.email
      }
    });
  }

  ngOnInit(): void {

    // Ambient eInvoice resolution trigger (Decision 9): fire whenever the
    // business id is present and *valid*, re-firing on change. Business-id
    // validation is synchronous, so status settles straight to VALID/INVALID;
    // watching status as well as value still matters because a country change
    // can flip FI/foreign validity without a value change. Gating on the *fresh*
    // control status (read in the handler, not the possibly-stale emitted tuple)
    // means a half-typed, INVALID id never fires a lookup, so this resolves "on
    // validity, not per keystroke" without a time-based debounce. patchEndCustomer
    // emits on load, so this covers the initial resolution too.
    const businessIdControl = this.form.get('endCustomer.business_id');
    this.einvoiceTriggerSub = combineLatest([
      businessIdControl.valueChanges.pipe(startWith(businessIdControl.value)),
      businessIdControl.statusChanges.pipe(startWith(businessIdControl.status)),
    ]).subscribe(() => this.onBusinessIdSettled());

    this.invoicePeerService
      .getInvoice()
      .subscribe((invoice: Invoice) => {
        this.invoice = invoice;
        this.load(invoice.end_customer_id, invoice.end_customer_contact_id);
      });

    this.invoicePeerService.getState().subscribe((state: string) => {
      if (state === 'locked') {
        this.locked = true;
      } else if (state === 'unlocked') {
        this.locked = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.einvoiceTriggerSub?.unsubscribe();
    this.cancelEinvoiceLookup();
  }

  load(endCustomerId: number, endCustomerContactId?: number): void {
    if (endCustomerId != null) {
      // Load endCustomer
      this.endCustomersService.getEndCustomer({id: endCustomerId}).subscribe((endCustomer: EndCustomer) => {
        this.endCustomer = endCustomer;
        this.patchEndCustomer(this.endCustomer);
      });
      // Load contacts
      this.endCustomersService.getEndCustomerContacts({end_customer_id: endCustomerId}).subscribe((contacts: EndCustomerContact[]) => {
        this.endCustomerContacts = contacts;
        if (endCustomerContactId) {
          // Select given contact
          this.endCustomerContact = this.endCustomerContacts.find(c => c.id === endCustomerContactId);
        } else if (contacts.length > 0) {
          // Select first contact
          this.endCustomerContact = contacts[0];
        } else {
          this.endCustomerContact = null;
        }

        if (this.endCustomerContact) {
          this.patchEndCustomerContact(this.endCustomerContact);
        }
      });
    } else {
      this.endCustomer = null;
      this.endCustomerContacts = [];
      this.endCustomerContact = null;
    }
  }

  getEndCustomer(endCustomerId: number): Observable<EndCustomer> {
    return this.endCustomersService.getEndCustomer({id: endCustomerId});
  }

  getEndCustomerContacts(endCustomerId: number): Observable<EndCustomerContact[]> {
    return this.endCustomersService.getEndCustomerContacts({end_customer_id: endCustomerId});
  }

  touchCreditLimitAndRating(endCustomer: EndCustomer) {
    if (!endCustomer.id || this.edited) {
      return;
    }

    if (endCustomer.credit_limit) {
      endCustomer.credit_limit.mark_checked = true;
    }

    if (endCustomer.aaa_rating) {
      endCustomer.aaa_rating.mark_checked = true;
    }

    this.endCustomersService
      .updateEndCustomer({ id: endCustomer.id, body: endCustomer })
      .subscribe((updatedEndCustomer: EndCustomer) => {
        // Assign updated endCustomer to current endCustomer
        Object.assign(this.endCustomer, updatedEndCustomer);
      });
  }
}
